import { z } from 'zod';

/**
 * Partenaires sous-traitants de BCL (ex : Solunova).
 *
 * Un partenaire dispose d'un compte dédié (rôle `partenaire`). Avant de
 * travailler, il complète un profil de conformité : il téléverse ses pièces
 * (KBIS, attestation URSAF, assurance RC, statuts, RIB…) en renseignant pour
 * chacune une date de validité. Les pièces qui expirent déclenchent un rappel
 * automatique pour mise à jour.
 */

export const partnerComplianceDocTypeSchema = z.enum([
  'kbis',
  'attestation_urssaf',
  'assurance_rc',
  'statuts',
  'rib',
  'piece_identite_dirigeant',
  'autre',
]);
export type PartnerComplianceDocType = z.infer<typeof partnerComplianceDocTypeSchema>;

/** Statut d'une pièce de conformité. `expire` est dérivé de la date de validité. */
export const partnerComplianceStatusSchema = z.enum([
  'en_attente',
  'valide',
  'refuse',
  'expire',
]);
export type PartnerComplianceStatus = z.infer<typeof partnerComplianceStatusSchema>;

/** Statut global du profil partenaire (dérivé de l'état des pièces). */
export const partnerProfileStatusSchema = z.enum([
  'incomplet',
  'en_verification',
  'valide',
  'suspendu',
]);
export type PartnerProfileStatus = z.infer<typeof partnerProfileStatusSchema>;

const siretRegex = /^\d{14}$/;
const phoneRegex = /^[+0-9 ().-]{6,20}$/;

// --- Onboarding (back-office, permission partners.manage) ---

export const createPartnerSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(8).max(200),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  phone: z.string().trim().regex(phoneRegex),
  companyName: z.string().trim().min(1).max(200),
  siret: z.string().trim().regex(siretRegex, 'SIRET invalide (14 chiffres)').optional(),
});
export type CreatePartnerInput = z.infer<typeof createPartnerSchema>;

// --- Profil (édité par le partenaire) ---

export const upsertPartnerProfileSchema = z.object({
  companyName: z.string().trim().min(1).max(200),
  siret: z.string().trim().regex(siretRegex, 'SIRET invalide (14 chiffres)').nullish(),
  legalForm: z.string().trim().max(120).nullish(),
  address: z.string().trim().max(300).nullish(),
});
export type UpsertPartnerProfileInput = z.infer<typeof upsertPartnerProfileSchema>;

export const partnerProfileSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  companyName: z.string(),
  siret: z.string().nullable(),
  legalForm: z.string().nullable(),
  address: z.string().nullable(),
  status: partnerProfileStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PartnerProfile = z.infer<typeof partnerProfileSchema>;

// --- Pièces de conformité (upload presigné S3) ---

export const requestComplianceUploadSchema = z.object({
  docType: partnerComplianceDocTypeSchema,
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(120),
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
  // Dates au format ISO (jour). `expiresAt` pilote les rappels (null = sans expiration).
  issuedAt: z.string().date().nullish(),
  expiresAt: z.string().date().nullish(),
});
export type RequestComplianceUploadInput = z.infer<typeof requestComplianceUploadSchema>;

export const reviewComplianceDocSchema = z
  .object({
    status: z.enum(['valide', 'refuse']),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((data) => data.status === 'valide' || !!data.reason, {
    message: 'Motif requis pour un refus',
    path: ['reason'],
  });
export type ReviewComplianceDocInput = z.infer<typeof reviewComplianceDocSchema>;

export const partnerComplianceDocSchema = z.object({
  id: z.string().uuid(),
  partnerId: z.string().uuid(),
  docType: partnerComplianceDocTypeSchema,
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  s3Key: z.string(),
  status: partnerComplianceStatusSchema,
  reviewReason: z.string().nullable(),
  issuedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  uploadedAt: z.string().datetime(),
  reviewedAt: z.string().datetime().nullable(),
});
export type PartnerComplianceDoc = z.infer<typeof partnerComplianceDocSchema>;
